import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import { FaEdit } from 'react-icons/fa';
import { MdDragIndicator } from 'react-icons/md';
import { API } from '../../api/Api';
import RichLabelEditor from '../components/RichLabelEditor';
import { sanitizeRichText } from '../../utils/richText';
import toast, { Toaster } from 'react-hot-toast';
import { BeatLoader } from 'react-spinners';

import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

const Collections = () => {
  const [loading, setLoading] = useState(false);
  const [apiData, setApiData] = useState([]);
  const [isEditMode, setIsEditMode] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [hasOrderChanged, setHasOrderChanged] = useState(false);

  const token = localStorage.getItem('token');
  const navigate = useNavigate();

  const [newCollection, setNewCollection] = useState({
    id: 0,
    name: '',
    label: '',
    code: '',
    api_collection_id: '',
    status: true,
  });

  /* ---------------- FETCH LIST ---------------- */
  const fetchCollectionList = async () => {
    const url = `${process.env.REACT_APP_API_URL}/visualizer/getcollectionlist`;
    const payload = { status: 'Active,De-active' };

    try {
      setLoading(true);
      const response = await API(url, payload, token);
      if (response?.Data) {
        setApiData(response.Data);
        setHasOrderChanged(false); 
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCollectionList();
  }, []);

  
  const onDragEnd = (result) => {
    if (!result.destination) return;

    const items = Array.from(apiData);
    const [moved] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, moved);

    setApiData(items);
    setHasOrderChanged(true); 
  };

  /* ---------------- SAVE ORDER ---------------- */
  const saveOrder = async () => {
     const ids = apiData.map(item => item.id);

 
  const payload = {
    ids: ids.join(',') 
  };
    
    const url = `${process.env.REACT_APP_API_URL}/visualizer/updatecollectionorder`;

    try {
      setLoading(true);
      const response = await API(url, payload, token); 
      if (response?.Result === 1) {
         toast.success('Collections Order saved successfully');
         setHasOrderChanged(false);
      }
      
    } catch (err) {
      toast.error('Failed to save order');
    } finally {
      setLoading(false);
    }
  };

  const handleAddCollection = async () => {
        const payload = {
            id: newCollection.id || null,
            name: newCollection.name,
            label: newCollection.label || '',
            code: newCollection.code,
            // 0 is a real id to the API, but `|| null` treated it as "no value"
            // and wiped it on every save. Only a genuinely empty field is null.
            api_collection_id:
                newCollection.api_collection_id === '' ||
                newCollection.api_collection_id === null ||
                newCollection.api_collection_id === undefined
                    ? null
                    : Number(newCollection.api_collection_id),
            status: newCollection.status ? 'Active' : 'De-active',
        };
      console.log('Saving collection with payload:', payload);
        const url = `${process.env.REACT_APP_API_URL}/visualizer/createupdatecollection`;

        try {
            setLoading(true);
            const response = await API(url, payload, token);
            if (response?.Result === 1) {
                toast.success(isEditMode ? 'Collection updated' : 'Collection added');
                fetchCollectionList(); // refresh list
                setShowAddModal(false);
                setNewCollection({ name: '', label: '', code: '', parent_id: '', api_collection_id: '', status: true });
                setIsEditMode(false);
            } else {
                toast.error('Failed to save collection');
            }
        } catch (error) {
            console.error('Collection save error:', error);
            toast.error('Something went wrong');
        } finally {
            setLoading(false);
        }
    };

  return (
    <>
      <Toaster position="top-center" />

      {loading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
          <BeatLoader size={18} />
        </div>
      )}

      <div className="flex h-screen fixed w-full bg-gray-100">
        <div className="md:w-64 w-16 bg-gray-800">
          <Sidebar />
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <div className="bg-white rounded-xl shadow-md">

            <div className="flex justify-between items-center p-4">
              <h2 className="text-xl font-semibold text-teal-600">
                Collections
              </h2>

              <div className="flex gap-3">
                {hasOrderChanged && (
                  <button
                    onClick={saveOrder}
                    className="bg-gradient-to-r from-teal-400 to-teal-500 text-white px-8 py-2 rounded shadow 
 hover:from-teal-500 hover:to-teal-600 
 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Save Changes
                  </button>
                )}

                <button
                  className="bg-teal-500 text-white px-4 py-2 rounded-lg"
                  onClick={() => {
                    setNewCollection({
                      id: 0,
                      name: '',
                      label: '',
                      code: '',
                      status: true,
                    });
                    setIsEditMode(false);
                    setShowAddModal(true);
                  }}
                >
                  Add New Collection
                </button>
              </div>
            </div>

            <div className="px-4 pb-4">
              <DragDropContext onDragEnd={onDragEnd}>
                <table className="w-full border">
                  <thead className="bg-blue-100">
                    <tr>
                      <th className="p-3 w-10"></th>
                      <th className="p-3 text-left">Name</th>
                      <th className="p-3 text-left">Label</th>
                      <th className="p-3 text-left">Code</th>
                      <th className="p-3 text-left">Status</th>
                      <th className="p-3 text-left">Actions</th>
                    </tr>
                  </thead>

                  <Droppable droppableId="collections">
                    {(provided) => (
                      <tbody
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                      >
                        {apiData.map((item, index) => (
                          <Draggable
                            key={item.id}
                            draggableId={String(item.id)}
                            index={index}
                          >
                            {(provided) => (
                              <tr
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                className="border-b hover:bg-gray-50"
                              >
                                {/* DRAG HANDLE */}
                                <td className="p-3 text-gray-400">
                                  <span
                                    {...provided.dragHandleProps}
                                    className="cursor-grab active:cursor-grabbing"
                                  >
                                    <MdDragIndicator size={22} />
                                  </span>
                                </td>

                                <td className="p-3">
                                  <button
                                    className="text-blue-600 hover:underline"
                                    onClick={() =>
                                      navigate(
                                        `/admin/collections/${item.id}/colors`,
                                        {
                                          state: {
                                            collectionName: item.name,
                                            colors: item.CollectionColor,
                                          },
                                        }
                                      )
                                    }
                                  >
                                    {item.name}
                                  </button>
                                </td>

                                <td className="p-3">
                                  {/* Exactly what was saved. collectionLabelMarkup is the
                                      visualizer's presentation of a label — it splits
                                      "X by Y", uppercases both halves and escapes any
                                      formatting — so the list was showing something the
                                      admin never typed. Sanitised, not raw, because it is
                                      stored HTML going through innerHTML. */}
                                  {item.label ? (
                                    <span dangerouslySetInnerHTML={{ __html: sanitizeRichText(item.label) }} />
                                  ) : (
                                    <span className="text-gray-400">—</span>
                                  )}
                                </td>

                                <td className="p-3">{item.code}</td>

                                <td className="p-3">
                                  <span
                                    className={`px-3 py-1 rounded text-white text-sm ${
                                      item.status === 'Active'
                                        ? 'bg-green-500'
                                        : 'bg-red-500'
                                    }`}
                                  >
                                    {item.status}
                                  </span>
                                </td>

                                <td className="p-3 flex gap-3">
                                  <FaEdit
                                    className="text-blue-500 cursor-pointer"
                                    onClick={() => {
                                      setNewCollection(item);
                                      setIsEditMode(true);
                                      setShowAddModal(true);
                                    }}
                                  />
                                </td>
                              </tr>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </tbody>
                    )}
                  </Droppable>
                </table>
              </DragDropContext>
                {showAddModal && (
                            <div className="fixed inset-0 z-50 bg-black bg-opacity-40 flex justify-center items-center">
                                <div className="bg-white p-6 rounded-xl w-full max-w-md shadow-xl relative">

                                    {/* Cross Button */}
                                    <button
                                        onClick={() => setShowAddModal(false)}
                                        className="absolute top-3 right-3 text-l font-bold   px-3 py-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                                    >
                                        ✕
                                    </button>

                                    <h2 className="text-lg font-semibold text-gray-800 mb-4">
                                        {isEditMode ? 'Edit Collection' : 'Add New Collection'}
                                    </h2>

                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-gray-700 mb-1">Name</label>
                                            <input
                                                type="text"
                                                value={newCollection.name}
                                                onChange={(e) =>
                                                    setNewCollection({ ...newCollection, name: e.target.value })
                                                }
                                                className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-400"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-gray-700 mb-1">Label</label>
                                            {/* Formatting buttons hidden for now, on request. Labels
                                                already saved with sub/sup/bold markup keep it — nothing
                                                strips them — it just cannot be edited from here until
                                                showToolbar comes back. */}
                                            <RichLabelEditor
                                                value={newCollection.label || ''}
                                                onChange={(label) =>
                                                    setNewCollection({ ...newCollection, label })
                                                }
                                                placeholder="e.g. SHINNOKI by DECOSPAN"
                                                showToolbar={false}
                                            />
                                            
                                        </div>
                                        <div>
                                            <label className="block text-gray-700 mb-1">Collection Code</label>
                                            <input
                                                type="text"
                                                value={newCollection.code}
                                                //disabled={isEditMode} // non-editable after creation
                                                onChange={(e) =>
                                                    setNewCollection({
                                                        ...newCollection,
                                                        code: e.target.value.replace(/\s+/g, '') // remove spaces
                                                    })
                                                }
                                                className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-400"
                                                placeholder="e.g. COLL123"
                                            />
                                        </div>
                                        <div>
    <label className="block text-gray-700 mb-1">API Collection ID</label>
    {/* ?? not ||: a stored 0 is a value, and `||` showed it as an empty field,
        so nobody could see what the collection actually had. */}
    <input
        type="number"
        value={newCollection.api_collection_id ?? ''}
        onChange={(e) =>
            setNewCollection({
                ...newCollection,
                api_collection_id: e.target.value
            })
        }
        className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-400"
        placeholder="Enter API Collection ID"
    />
</div>

                                        {/* Status Checkbox */}

                                        <div className="flex items-center gap-3">
                                            <label className="text-gray-700 font-medium">Status</label>
                                            <button
                                                onClick={() =>
                                                    setNewCollection({
                                                        ...newCollection,
                                                        status: newCollection.status === 'Active' ? 'De-active' : 'Active'
                                                    })
                                                }
                                                className={`relative w-14 h-7 flex items-center rounded-full transition-colors ${newCollection.status === 'Active' ? 'bg-teal-500' : 'bg-gray-300'
                                                    }`}
                                            >

                                                <span
                                                    className={`absolute left-1 w-5 h-5 bg-white rounded-full shadow-md transform transition-transform ${newCollection.status === 'Active' ? 'translate-x-7' : 'translate-x-0'
                                                        }`}
                                                />
                                            </button>

                                        </div>



                                        {/* Action Buttons */}
                                        <div className="flex justify-end gap-3 pt-4">
                                            <button
                                                onClick={handleAddCollection}
                                                className="p-2 bg-green-500 text-white rounded hover:bg-green-700"
                                            >
                                                {isEditMode ? 'Update' : 'Save'}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Collections;
